import { useTranslation } from 'react-i18next';
import PageHeader from '@casops/components/layout/page-header/PageHeader';
import {
  useGetUsersQuery,
  UserBasicModel,
  useRemoveUserMutation,
  useUnlockUserMutation,
} from '@casops/store/apis/usersApi';
import { useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import { columnDefModelActions } from '@casops/components/tables/grid/columnDefs';
import { ModelName, QueryTag, TagList } from '@casops/store/api';
import { SidePanel } from '@casops/store/slices/globalSlice';
import StandardPage from '@casops/templates/standard-page/StandardPage';
import { CollectionView } from '@casops/components/layout/collection-view/CollectionView';
import { useTableData } from '@casops/shared/hooks/useTableData';
import { Permission, useIsUserWithPermission } from '@casops/permissions';
import { ModelActionsCellRendererParams } from '@casops/components/tables/cells/model-actions/ModelActionsCellRenderer';
import CreateItemButton from '@casops/components/inputs/create-item-button/CreateItemButton';
import { getDefaultModelArg } from '@casops/components/tables/cells/model-actions/utils';

type Model = UserBasicModel;

const modelName = ModelName.User;
const sidePanel = SidePanel.ManageUser;
const permission = Permission.UserManagement;

const tagsToClearOnRefresh = [{ type: QueryTag.User, id: TagList }];

const canDeleteItem = (data: Model): boolean => !data.ro;
const canUnlockItem = (data: Model): boolean => data.locked;

const getItemName = (data: Model): string => {
  return `${data.fn} ${data.ln}`;
};

const cellRendererParams: ModelActionsCellRendererParams<Model> = {
  modelName,
  permission,
  unlockProps: {
    canUnlockItem,
    getQueryArg: getDefaultModelArg,
    useUnlockModelMutation: useUnlockUserMutation,
    getItemName,
  },
  editProps: {
    sidePanel,
    canEditItem: true,
  },
  deleteProps: {
    canDeleteItem,
    getQueryArg: getDefaultModelArg,
    useDeleteModelMutation: useRemoveUserMutation,
    getItemName,
  },
};

export function UsersPage() {
  const { t } = useTranslation();

  const isAllowedModelActions = useIsUserWithPermission(permission);

  const columnDefs = useMemo<ColDef<Model>[]>(() => {
    const columnDefs: ColDef<Model>[] = [
      { field: 'un', headerName: t('Fields.Username'), minWidth: 80 },
      { field: 'fn', headerName: t('Fields.FirstName'), minWidth: 80 },
      { field: 'ln', headerName: t('Fields.LastName'), minWidth: 80 },
      { field: 'em', headerName: t('Fields.Email'), minWidth: 120 },
    ];

    if (isAllowedModelActions) {
      columnDefs.push({
        ...columnDefModelActions<Model>(t, cellRendererParams),
        minWidth: 80,
        maxWidth: 120,
      });
    }

    return columnDefs;
  }, [t, isAllowedModelActions]);

  const noResultsDescription = t('Model.Collection.NoItems', { modelName });

  const { data, ...requestRemainder } = useGetUsersQuery(undefined, {
    refetchOnMountOrArgChange: 60,
  });

  const { hasData, noDataDescription } = useTableData<Model>({
    data,
    ...requestRemainder,
    noResultsDescription,
  });

  return (
    <StandardPage
      header={
        <PageHeader
          title={t('Model.Collection.Title', { modelName })}
          tagsToClearOnRefresh={tagsToClearOnRefresh}
        />
      }
    >
      <CollectionView<Model>
        sliceKey={modelName}
        modelName={modelName}
        columnDefs={columnDefs}
        rowData={data}
        hasData={hasData}
        noDataDescription={noDataDescription}
        actions={
          <CreateItemButton modelName={modelName} sidePanel={sidePanel} permission={permission} />
        }
      />
    </StandardPage>
  );
}

export default UsersPage;
